import { Box, Button, Icon, Text, Wrap } from "@chakra-ui/react";
import { ApiErrorProvider } from "@raiden/library/components/ReactHookForm/ApiError";
import CustomersFormContacts from "@raiden/library/containers/Customers/Form/FormContacts";
import useAuthAs from "@raiden/library/hooks/auth/as";
import { useApi } from "@raiden/library/hooks/useApi";
import generateApiUri from "@raiden/library/libraries/utils/generateApiUri";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { useState } from "react";
import { IoAddOutline } from "react-icons/io5";
import { FormattedMessage, useIntl } from "react-intl";
import Splash from "../../../components/Splash";
import SWRHandler from "../../../components/SWR/SWRHandler";
import CustomersContactsUpdateHandler from "../../Users/Update/Contacts/Handler";
import StepBottomBar from "../StepBottomBar";
import StepLayout from "../StepLayout";

/**
 * @typedef {Object} Props
 * @property {() => void} nextStep
 *
 * @param {Props} props
 */
function StepContacts({ nextStep }) {
  const intl = useIntl();

  const [forceShowContacts, setForceShowContacts] = useState(false);

  const { user } = useAuthAs();

  /** @type {import("@raiden/library/hooks/useApi").UseApi<import("@raiden/library/types/Customer").Customer>} */
  const { swrResponse: swrResponseCustomer } = useApi(
    user
      ? generateApiUri({
          id: "@customers.view",
          parameters: {
            customerId: user?.id,
          },
          query: {
            fields: ["user.customer.contacts", "customer.default_address"],
          },
        })
      : null,
  );

  return (
    <StepLayout
      title={intl.formatMessage({
        defaultMessage: "Personnes autorisées",
      })}>
      {user ? (
        <SWRHandler
          swrResponse={swrResponseCustomer}
          wrapper={(children) => <Box p="1rem">{children}</Box>}>
          {(response) => (
            <>
              {(response.data.contacts?.length ?? 0) > 0 ||
              forceShowContacts ? (
                <>
                  <Text colorScheme="gray" pt="1rem" px="1rem">
                    <FormattedMessage defaultMessage="Cette liste de contacts permet d'identifier auprès de nos services une liste de personnes à contacter en cas de besoin. Elle permet également, pour les enfants, de définir quelles personnes ont les autorisations pour venir les chercher à la sortie des activités." />
                  </Text>

                  <ApiErrorProvider>
                    <CustomersContactsUpdateHandler
                      customer={response.data}
                      initializeWithEmptyContact={true}
                      onSuccess={() => {
                        nextStep();
                      }}
                      render={({ isLoading }) => (
                        <>
                          <Box p="1rem">
                            <CustomersFormContacts
                              context="update"
                              customerId={user?.id}
                              withApiErrorDetail={true}
                              userContext="customer"
                            />
                          </Box>

                          <StepBottomBar>
                            <Button
                              type="submit"
                              isLoading={isLoading}
                              colorScheme="brandSecondary">
                              <FormattedMessage defaultMessage="Enregistrer" />
                            </Button>
                          </StepBottomBar>
                        </>
                      )}
                    />
                  </ApiErrorProvider>
                </>
              ) : (
                <Box p="1rem">
                  <Splash
                    src={generateFrontPath({
                      id: "internal-assets",
                      parameters: { filePath: "illustrations/empty.svg" },
                      includeBasePath: true,
                    })}
                    title={intl.formatMessage({
                      defaultMessage:
                        "Nous n'avez pas encore renseigné de personnes supplémentaires",
                    })}
                    description={intl.formatMessage(
                      {
                        defaultMessage:
                          "Cette liste permet d'identifier auprès de nos services une liste de personnes à contacter en cas de besoin. Elle permet également, pour les enfants, de définir quelles personnes ont les autorisations pour venir les chercher à la sortie des activités. Si vous n'ajoutez pas de personnes supplémentaire, la personne utilisé sera: {firstName} {lastName}, {phone}.",
                      },
                      {
                        firstName: response.data.first_name ?? "-",
                        lastName: response.data.last_name ?? "-",
                        phone:
                          response.data.default_address?.phone_number ?? "-",
                      },
                    )}
                    addon={
                      <Wrap justify="center" spacing="1rem">
                        <Button onClick={nextStep}>
                          <FormattedMessage defaultMessage="Passer cette étape" />
                        </Button>

                        <Button
                          onClick={() => {
                            setForceShowContacts(true);
                          }}
                          leftIcon={<Icon as={IoAddOutline} />}
                          colorScheme="brandPrimary">
                          <FormattedMessage defaultMessage="Ajouter une personne" />
                        </Button>
                      </Wrap>
                    }
                  />
                </Box>
              )}
            </>
          )}
        </SWRHandler>
      ) : (
        <Text colorScheme="red">
          <FormattedMessage defaultMessage="Impossible de récupérer les informations du compte courant nécessaires à l'affichage de la page." />
        </Text>
      )}
    </StepLayout>
  );
}

export default StepContacts;
